import React, { ReactNode, useState } from 'react';

import { noop } from 'lodash';

import TextButton from '~/core/atoms/Buttons/TextButton';
import EmptyState from '~/core/molecules/EmptyState/EmptyState';
import SkeletonTable from '~/core/molecules/SkeletonTable';
import cn from '~/utils/cn';

interface ListItem {
  icon?: ReactNode;
  content: ReactNode;
  id: string | number;
}

interface ExpandableListCardProps {
  title?: ReactNode;
  items: ListItem[];
  maxVisibleItems?: number;
  className?: string;
  onItemClick?: (item: ListItem) => void;
  emptyState?: ReactNode;
  isLoading?: boolean;
  isError?: boolean;
}

const DefaultEmptyState = <EmptyState title="No items found" />;
const DEFAULT_MAX_VISIBLE_ITEMS = 3;
const ExpandableListCard: React.FC<ExpandableListCardProps> = ({
  title,
  items,
  maxVisibleItems = DEFAULT_MAX_VISIBLE_ITEMS,
  className,
  onItemClick = noop,
  emptyState = DefaultEmptyState,
  isLoading = false,
  isError,
}) => {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const visibleItems = expanded ? items : items.slice(0, maxVisibleItems);
  const hasMoreItems = items.length > maxVisibleItems;
  const hasItems = !!items?.length;

  const handleItemClick = (item: ListItem) => {
    onItemClick(item);
  };

  return (
    <div
      className={cn(
        'flex w-full flex-col rounded-lg border border-0 text-base',
        { 'pb-20': hasItems && !hasMoreItems },
        className
      )}
    >
      {title ? (
        <div className="p-6 pb-0">
          <div className="text-base font-semibold">{title}</div>
        </div>
      ) : null}

      <div className="flex-1 px-16">
        {isLoading ? (
          <div className="flex h-full w-full justify-center">
            <SkeletonTable rowsCount={maxVisibleItems} columnsCount={2} cellClassName="h-8" isError={isError} />
          </div>
        ) : (
          <>
            {visibleItems?.length ? (
              <ul>
                {visibleItems.map((item) => (
                  <li
                    key={item.id}
                    className="flex cursor-pointer items-center gap-12 border-b border-blue-200/50 py-12 font-normal transition-colors duration-200 hover:bg-slate-100"
                    onClick={() => handleItemClick(item)}
                  >
                    {item.icon && <div className="flex-shrink-0 pl-8">{item.icon}</div>}
                    <div className="flex flex-1 overflow-hidden">{item.content}</div>
                  </li>
                ))}
              </ul>
            ) : (
              emptyState
            )}
          </>
        )}
      </div>

      {hasMoreItems && (
        <div className="mt-auto flex justify-center p-8">
          <TextButton onClick={toggleExpanded} className="text-base text-blue-900 underline hover:text-blue-500">
            {expanded ? 'Show less' : 'Show all'}
          </TextButton>
        </div>
      )}
    </div>
  );
};

export default ExpandableListCard;
